import React, { useState, useEffect } from 'react';
import { BookOpen, Plus, Edit, Trash2, AlertCircle, CheckCircle, Search, Building, Eye, Award, FileText } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import AdminGlobalLayout from '../../components/AdminGlobalLayout';
import AdminDataTable from '../../components/AdminDataTable';

interface Course {
  id: string;
  title: string;
  description: string;
  cover_image: string;
  duration: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  is_active: boolean;
  created_at: string;
  igreja_id: string;
  igreja_nome?: string;
  lessons_count?: number;
  students_count?: number;
}

interface Igreja {
  id: string;
  nome: string;
  cidade: string;
  estado: string;
}

interface CourseFormData {
  title: string;
  description: string;
  cover_image: string;
  duration: string;
  level: 'beginner' | 'intermediate' | 'advanced';
  is_active: boolean;
  igreja_id: string;
}

export default function AdminCourses() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [igrejas, setIgrejas] = useState<Igreja[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<Course | null>(null);
  const [courseToDelete, setCourseToDelete] = useState<Course | null>(null);
  const [viewingCourse, setViewingCourse] = useState<Course | null>(null);
  const [formData, setFormData] = useState<CourseFormData>({
    title: '',
    description: '',
    cover_image: '',
    duration: '',
    level: 'beginner',
    is_active: true,
    igreja_id: ''
  });
  
  // Table state
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string | null>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>('desc');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCourses, setFilteredCourses] = useState<Course[]>([]);
  const [selectedIgreja, setSelectedIgreja] = useState<string>('');
  const [activeFilter, setActiveFilter] = useState<boolean | null>(null);

  useEffect(() => {
    fetchCourses();
    fetchIgrejas();
  }, []);

  useEffect(() => {
    // Filter and sort courses
    let filtered = [...courses];
    
    // Apply igreja filter
    if (selectedIgreja) {
      filtered = filtered.filter(course => course.igreja_id === selectedIgreja);
    }
    
    // Apply active filter
    if (activeFilter !== null) {
      filtered = filtered.filter(course => course.is_active === activeFilter);
    }
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(course => 
        course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.igreja_nome?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sorting
    if (sortField && sortOrder) {
      filtered.sort((a, b) => {
        let aValue, bValue;
        
        if (sortField === 'igreja_nome') {
          aValue = a.igreja_nome || '';
          bValue = b.igreja_nome || '';
        } else {
          aValue = a[sortField as keyof Course];
          bValue = b[sortField as keyof Course];
        }
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortOrder === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
          return sortOrder === 'asc'
            ? (aValue === bValue ? 0 : aValue ? 1 : -1)
            : (aValue === bValue ? 0 : aValue ? -1 : 1);
        }
        
        return 0;
      });
    }
    
    setFilteredCourses(filtered);
  }, [courses, searchTerm, sortField, sortOrder, selectedIgreja, activeFilter]);

  async function fetchCourses() {
    try {
      setLoading(true);
      
      // Fetch courses
      const { data: coursesData, error: coursesError } = await supabase
        .from('courses')
        .select('*');
      
      if (coursesError) throw coursesError;
      
      // Fetch igrejas to get names
      const { data: igrejasData, error: igrejasError } = await supabase
        .from('igrejas')
        .select('id, nome');
      
      if (igrejasError) throw igrejasError;
      
      // Fetch lesson counts
      const { data: lessonsData, error: lessonsError } = await supabase
        .from('course_lessons')
        .select('course_id, count')
        .group('course_id');
      
      if (lessonsError) throw lessonsError;
      
      // Fetch student counts
      const { data: studentsData, error: studentsError } = await supabase
        .from('course_progress')
        .select('course_id, count')
        .group('course_id');
      
      if (studentsError) throw studentsError;
      
      // Map igreja names and counts to courses
      const coursesWithDetails = coursesData?.map(course => {
        const igreja = igrejasData?.find(i => i.id === course.igreja_id);
        const lessonCount = lessonsData?.find(l => l.course_id === course.id)?.count || 0;
        const studentCount = studentsData?.find(s => s.course_id === course.id)?.count || 0;
        
        return {
          ...course,
          igreja_nome: igreja?.nome || 'Sem igreja',
          lessons_count: parseInt(lessonCount as any),
          students_count: parseInt(studentCount as any)
        };
      }) || [];
      
      setCourses(coursesWithDetails);
    } catch (error: any) {
      setError(error.message || 'Erro ao buscar cursos');
    } finally {
      setLoading(false);
    }
  }

  async function fetchIgrejas() {
    try {
      const { data, error } = await supabase
        .from('igrejas')
        .select('id, nome, cidade, estado')
        .order('nome');

      if (error) throw error;
      setIgrejas(data || []);
    } catch (error: any) {
      setError(error.message || 'Erro ao buscar igrejas');
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      if (editingCourse) {
        // Update existing course
        const { error } = await supabase
          .from('courses')
          .update(formData)
          .eq('id', editingCourse.id);
        
        if (error) throw error;
        setSuccess('Curso atualizado com sucesso!');
      } else {
        // Create new course
        const { error } = await supabase
          .from('courses')
          .insert([formData]);
        
        if (error) throw error;
        setSuccess('Curso criado com sucesso!');
      }
      
      // Reset form and close modal
      setFormData({
        title: '',
        description: '',
        cover_image: '',
        duration: '',
        level: 'beginner',
        is_active: true,
        igreja_id: ''
      });
      setIsModalOpen(false);
      setEditingCourse(null);
      
      // Refresh data
      fetchCourses();
    } catch (error: any) {
      setError(error.message || 'Erro ao salvar curso');
    }
  };

  const handleEdit = (course: Course) => {
    setEditingCourse(course);
    setFormData({
      title: course.title,
      description: course.description,
      cover_image: course.cover_image,
      duration: course.duration,
      level: course.level,
      is_active: course.is_active,
      igreja_id: course.igreja_id
    });
    setIsModalOpen(true);
  };

  const handleView = (course: Course) => {
    setViewingCourse(course);
    setIsViewModalOpen(true);
  };

  const handleDelete = async () => {
    if (!courseToDelete) return;
    
    try {
      setError('');
      setSuccess('');
      
      // Check if there are students enrolled in this course
      if (courseToDelete.students_count && courseToDelete.students_count > 0) {
        setError(`Não é possível excluir este curso pois existem ${courseToDelete.students_count} alunos matriculados.`);
        setIsDeleteModalOpen(false);
        setCourseToDelete(null);
        return;
      }
      
      // Delete course
      const { error } = await supabase
        .from('courses')
        .delete()
        .eq('id', courseToDelete.id);
      
      if (error) throw error;
      
      setSuccess('Curso excluído com sucesso!');
      setIsDeleteModalOpen(false);
      setCourseToDelete(null);
      
      // Refresh data
      fetchCourses();
    } catch (error: any) {
      setError(error.message || 'Erro ao excluir curso');
    }
  };

  const handleSortingChange = (field: string, order: 'asc' | 'desc') => {
    setSortField(field);
    setSortOrder(order);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const getLevelLabel = (level: string) => {
    switch (level) {
      case 'beginner': return 'Iniciante';
      case 'intermediate': return 'Intermediário';
      case 'advanced': return 'Avançado';
      default: return level;
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-blue-100 text-blue-800';
      case 'advanced': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const columns = [
    {
      header: 'Curso',
      accessorKey: 'title',
      enableSorting: true,
      cell: (info: { row: Course }) => (
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100">
            <img 
              src={info.row.cover_image} 
              alt={info.row.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/100?text=Sem+Imagem';
              }}
            />
          </div>
          <div>
            <div className="font-medium text-gray-900">{info.row.title}</div>
            <div className="text-sm text-gray-500 line-clamp-1">{info.row.description}</div>
          </div>
        </div>
      )
    },
    {
      header: 'Igreja',
      accessorKey: 'igreja_nome',
      enableSorting: true,
    },
    {
      header: 'Nível',
      accessorKey: 'level',
      enableSorting: true,
      cell: (info: { row: Course }) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLevelColor(info.row.level)}`}>
          {getLevelLabel(info.row.level)}
        </span>
      )
    },
    {
      header: 'Status',
      accessorKey: 'is_active',
      enableSorting: true,
      cell: (info: { row: Course }) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          info.row.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
          {info.row.is_active ? 'Ativo' : 'Inativo'}
        </span>
      )
    },
    {
      header: 'Estatísticas',
      accessorKey: 'lessons_count',
      cell: (info: { row: Course }) => (
        <div className="text-sm">
          <div className="flex items-center gap-1">
            <FileText className="h-4 w-4 text-gray-500" />
            <span>{info.row.lessons_count || 0} aulas</span>
          </div>
          <div className="flex items-center gap-1 mt-1">
            <Award className="h-4 w-4 text-gray-500" />
            <span>{info.row.students_count || 0} alunos</span>
          </div>
        </div>
      )
    },
    {
      header: 'Ações',
      accessorKey: 'id',
      cell: (info: { row: Course }) => (
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => handleView(info.row)}
            className="p-1 text-brand-teal hover:text-brand-teal/80 rounded-full"
            title="Visualizar"
          >
            <Eye className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleEdit(info.row)}
            className="p-1 text-brand-teal hover:text-brand-teal/80 rounded-full"
            title="Editar"
          >
            <Edit className="h-5 w-5" />
          </button>
          <button
            onClick={() => {
              setCourseToDelete(info.row);
              setIsDeleteModalOpen(true);
            }}
            className="p-1 text-red-500 hover:text-red-600 rounded-full"
            title="Excluir"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      )
    }
  ];

  // Calculate pagination
  const pageCount = Math.ceil(filteredCourses.length / pageSize);
  const paginatedData = filteredCourses.slice(
    pageIndex * pageSize,
    (pageIndex + 1) * pageSize
  );

  return (
    <AdminGlobalLayout title="Gerenciar Cursos">
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-lg flex items-start">
          <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{success}</span>
        </div>
      )}

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label htmlFor="igreja_filter" className="block text-sm font-medium text-gray-700 mb-1">
            Filtrar por Igreja
          </label>
          <select
            id="igreja_filter"
            value={selectedIgreja}
            onChange={(e) => setSelectedIgreja(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
          >
            <option value="">Todas as Igrejas</option>
            {igrejas.map((igreja) => (
              <option key={igreja.id} value={igreja.id}>
                {igreja.nome} - {igreja.cidade}/{igreja.estado}
              </option>
            ))}
          </select>
        </div>
        
        <div>
          <label htmlFor="active_filter" className="block text-sm font-medium text-gray-700 mb-1">
            Filtrar por Status
          </label>
          <select
            id="active_filter"
            value={activeFilter === null ? '' : activeFilter ? 'true' : 'false'}
            onChange={(e) => {
              if (e.target.value === '') {
                setActiveFilter(null);
              } else {
                setActiveFilter(e.target.value === 'true');
              }
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
          >
            <option value="">Todos os Status</option>
            <option value="true">Ativos</option>
            <option value="false">Inativos</option>
          </select>
        </div>
      </div>

      <AdminDataTable
        data={paginatedData}
        columns={columns}
        title="Cursos"
        subtitle={`Total de ${filteredCourses.length} cursos cadastrados`}
        loading={loading}
        pagination={{
          pageIndex,
          pageSize,
          pageCount,
          onPageChange: setPageIndex
        }}
        sorting={{
          field: sortField,
          order: sortOrder,
          onSortingChange: handleSortingChange
        }}
        filtering={{
          globalFilter: searchTerm,
          onGlobalFilterChange: setSearchTerm
        }}
        enableExport={true}
        exportFileName="cursos"
        actions={
          <button
            onClick={() => {
              setEditingCourse(null);
              setFormData({
                title: '',
                description: '',
                cover_image: '',
                duration: '',
                level: 'beginner',
                is_active: true,
                igreja_id: ''
              });
              setIsModalOpen(true);
            }}
            className="px-3 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors flex items-center gap-2 text-sm"
          >
            <Plus className="h-4 w-4" />
            <span>Novo Curso</span>
          </button>
        }
      />

      {/* Course Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-brand-teal mb-6">
              {editingCourse ? 'Editar Curso' : 'Novo Curso'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Título do Curso *
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label htmlFor="cover_image" className="block text-sm font-medium text-gray-700 mb-1">
                    URL da Imagem de Capa *
                  </label>
                  <input
                    type="url"
                    id="cover_image"
                    name="cover_image"
                    value={formData.cover_image}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                    placeholder="https://exemplo.com/imagem.jpg"
                  />
                </div>
                
                <div>
                  <label htmlFor="duration" className="block text-sm font-medium text-gray-700 mb-1">
                    Duração *
                  </label>
                  <input
                    type="text"
                    id="duration"
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                    placeholder="Ex: 4 semanas"
                  />
                </div>
                
                <div>
                  <label htmlFor="level" className="block text-sm font-medium text-gray-700 mb-1">
                    Nível *
                  </label>
                  <select
                    id="level"
                    name="level"
                    value={formData.level}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  >
                    <option value="beginner">Iniciante</option>
                    <option value="intermediate">Intermediário</option>
                    <option value="advanced">Avançado</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="is_active" className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    <input
                      type="checkbox"
                      id="is_active"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className="mr-2 rounded border-gray-300 text-brand-teal focus:ring-brand-teal"
                    />
                    Curso Ativo
                  </label>
                </div>
                
                <div>
                  <label htmlFor="igreja_id" className="block text-sm font-medium text-gray-700 mb-1">
                    Igreja *
                  </label>
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <select
                      id="igreja_id"
                      name="igreja_id"
                      value={formData.igreja_id}
                      onChange={handleInputChange}
                      required
                      className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                    >
                      <option value="">Selecione uma igreja...</option>
                      {igrejas.map((igreja) => (
                        <option key={igreja.id} value={igreja.id}>
                          {igreja.nome} - {igreja.cidade}/{igreja.estado}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
                >
                  {editingCourse ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Course Modal */}
      {isViewModalOpen && viewingCourse && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-xl font-semibold text-brand-teal">{viewingCourse.title}</h2>
              <button
                onClick={() => setIsViewModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="aspect-video w-full rounded-lg overflow-hidden bg-gray-100">
                <img 
                  src={viewingCourse.cover_image} 
                  alt={viewingCourse.title}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/800x450?text=Sem+Imagem';
                  }}
                />
              </div>
              
              <div className="flex flex-wrap gap-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getLevelColor(viewingCourse.level)}`}>
                  {getLevelLabel(viewingCourse.level)}
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  viewingCourse.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {viewingCourse.is_active ? 'Ativo' : 'Inativo'}
                </span>
                <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">
                  {viewingCourse.duration}
                </span>
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-500">Descrição</h3>
                <p className="mt-2 text-gray-900">{viewingCourse.description}</p>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Building className="h-5 w-5 text-brand-teal" />
                    <h3 className="font-medium text-gray-900">Igreja</h3>
                  </div>
                  <p className="text-gray-700">{viewingCourse.igreja_nome}</p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="h-5 w-5 text-brand-teal" />
                    <h3 className="font-medium text-gray-900">Aulas</h3>
                  </div>
                  <p className="text-gray-700">{viewingCourse.lessons_count || 0} aulas</p>
                </div>
                
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Award className="h-5 w-5 text-brand-teal" />
                    <h3 className="font-medium text-gray-900">Alunos</h3>
                  </div>
                  <p className="text-gray-700">{viewingCourse.students_count || 0} alunos</p>
                </div>
              </div>
              
              <div className="flex justify-between pt-4">
                <button
                  onClick={() => {
                    setIsViewModalOpen(false);
                    handleEdit(viewingCourse);
                  }}
                  className="px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors flex items-center gap-2"
                >
                  <Edit className="h-4 w-4" />
                  Editar Curso
                </button>
                <button
                  onClick={() => setIsViewModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && courseToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmar exclusão</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir o curso <strong>{courseToDelete.title}</strong>? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setCourseToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminGlobalLayout>
  );
}